import { calculatePlotAreaHa } from "../../../shared/utils/areaCalculation";

export interface NutrientPlot {
  id: string;
  label: string;
  areaHa: number;
}

export interface NutrientGroup {
  id: string;
  // Position among the trial's plot groups; strip trials have a single group
  // and no letter.
  groupIndex?: number;
  plots: NutrientPlot[];
}

// Subset of the wizard's trial-plot model used here, typed locally so the page
// does not import the wizard's types.
interface TrialPlotLike {
  plot?: {
    id?: number;
    rows?: number;
    columns?: number;
    isStripPlot?: boolean;
    column_size?: number;
    row_size?: number;
    plotWidth?: number;
    plotHeight?: number;
    stripArea?: number;
    stripLength?: number;
    stripWidth?: number;
  };
}

interface StripsDataLike {
  isStripMode?: boolean;
  stripDetails?: Array<{
    area?: number;
    stripLength?: number;
    stripWidth?: number;
  }>;
}

export const groupLetter = (groupIndex: number): string =>
  String.fromCodePoint(65 + groupIndex);

const toCount = (value: number | undefined): number =>
  Number.isInteger(value) && (value as number) > 0 ? (value as number) : 0;

// Turns the trial's saved plot design into the groups the Nutrient tables list:
// one group per plot group, one row per cell (rows x columns) numbered from 1,
// with the same "{groupId}-{row}-{col}" ids the wizard uses for rates. A strip
// trial is a single group of strips. Replicants repeat their group's rates, so
// they do not get rows of their own.
export const buildNutrientGroups = (
  trialPlots: TrialPlotLike[] | undefined,
  stripsData?: StripsDataLike | null
): NutrientGroup[] => {
  const source = trialPlots ?? [];
  const isStripMode = stripsData?.isStripMode === true;

  return source.flatMap((trialPlot, groupIndex): NutrientGroup[] => {
    const groupId = trialPlot.plot?.id;
    const rows = toCount(trialPlot.plot?.rows);
    const columns = toCount(trialPlot.plot?.columns);
    if (groupId === undefined || rows === 0 || columns === 0) {
      return [];
    }

    const plots = Array.from({ length: rows * columns }, (_, cell) => {
      const row = Math.floor(cell / columns);
      const column = cell % columns;
      const id = `${groupId}-${row}-${column}`;
      return {
        id,
        label: String(cell + 1),
        areaHa: calculatePlotAreaHa(
          id,
          source,
          stripsData ?? undefined,
          groupIndex
        ),
      };
    });

    return [
      {
        id: String(groupId),
        groupIndex: isStripMode ? undefined : groupIndex,
        plots,
      },
    ];
  });
};
